
import React, { useState, useEffect, useRef } from "react"
import BackgroundImageOne from "../../assets/practices.png"
import Business from "../../assets/business.png"
import Learning from "../../assets/learning.jpeg"
import GainConfidence from "../../assets/gain-confidence.jpeg"
import RegisterPublish from "../../assets/Register-publish.jpeg"
import { ChevronLeft, ChevronRight } from "lucide-react"
import { Link } from "react-router-dom"

const BASE_CARDS = [
  { title: "Turn Practice into performance. WAEC, NECO, JAMB",                             image: BackgroundImageOne, href: "https://study.filloptech.com/"     },
  { title: "Explore our Businesses; from physical to online",                               image: Business,          href: "https://business.filloptech.com/"  },
  { title: "Gain confidence with CBT Guru for JAMB, WAEC, NECO",                           image: GainConfidence,    href: "https://business.filloptech.com/marketplace" },
  { title: "Learn essential career and life skills through our Professional Courses (LMS)", image: Learning,          href: "https://filloptech.com/login" },
  { title: "Become a Resource Person, publish your educational materials and start earning",  image: RegisterPublish,   href: "https://filloptech.com/resource-person"     },
]

const TOTAL  = BASE_CARDS.length
const OFFSET = TOTAL
const cards  = [...BASE_CARDS, ...BASE_CARDS, ...BASE_CARDS]


// Compute card sizing from the current viewport. Used both to initialise state
// (so the very first paint is already correct — no resize flash/shrink) and on
// window resize.
function computeLayout() {
  const w = typeof window === "undefined" ? 1024 : window.innerWidth

  // Mobile: wider cards, SHORTER image so the full caption fits below it
  if      (w < 360) return { cardWidth: w - 32, imageHeight: 150, isDesktop: false }
  else if (w < 480) return { cardWidth: w - 40, imageHeight: 170, isDesktop: false }
  else if (w < 640) return { cardWidth: w - 60, imageHeight: 180, isDesktop: false }
  else if (w < 768) return { cardWidth: w - 80, imageHeight: 190, isDesktop: false }
  // Desktop: keep original proportions
  return { cardWidth: 500, imageHeight: 190, isDesktop: true }
}

export default function HeroSection() {
  const initial = computeLayout()
  const [index,       setIndex]       = useState(OFFSET)
  const [animated,    setAnimated]    = useState(true)
  const [cardWidth,   setCardWidth]   = useState(initial.cardWidth)
  const [cardHeight,  setCardHeight]  = useState(280) // desktop card height
  const [imageHeight, setImageHeight] = useState(initial.imageHeight)
  const [isDesktop,   setIsDesktop]   = useState(initial.isDesktop)
  const [sliderTop,   setSliderTop]   = useState(0)

  const touchStartX = useRef(null)
  const sliderRef   = useRef(null)

  useEffect(() => {
    const update = () => {
      const next = computeLayout()
      setIsDesktop(next.isDesktop)
      setCardWidth(next.cardWidth)
      setCardHeight(280)
      setImageHeight(next.imageHeight)
    }
    update()
    window.addEventListener("resize", update)
    return () => window.removeEventListener("resize", update)
  }, [])

  // measure slider top position so we can centre arrows over it
  useEffect(() => {
    if (!sliderRef.current) return
    const measure = () => {
      const rect = sliderRef.current.getBoundingClientRect()
      const parentRect = sliderRef.current.closest("section").getBoundingClientRect()
      setSliderTop(rect.top - parentRect.top + rect.height / 2)
    }
    measure()
    window.addEventListener("resize", measure)
    return () => window.removeEventListener("resize", measure)
  }, [cardWidth, imageHeight, isDesktop])

  const GAP       = 24
  const CARD_STEP = cardWidth + GAP

  const next = () => setIndex(i => i + 1)
  const prev = () => setIndex(i => i - 1)

  useEffect(() => {
    const t = setInterval(next, 5000)
    return () => clearInterval(t)
  }, [])

  useEffect(() => {
    if (index >= TOTAL * 2) {
      const t = setTimeout(() => { setAnimated(false); setIndex(OFFSET) }, 300)
      return () => clearTimeout(t)
    }
    if (index < OFFSET) {
      const t = setTimeout(() => { setAnimated(false); setIndex(TOTAL * 2 - 1) }, 300)
      return () => clearTimeout(t)
    }
  }, [index])

  // After a wrap-around jump (transition disabled), wait a full frame for the
  // untransitioned transform to commit before restoring the animation — this
  // avoids the brief snap-back/shrink frame the 50ms timer could leave visible.
  useEffect(() => {
    if (animated) return undefined
    let raf1
    let raf2
    raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => setAnimated(true))
    })
    return () => {
      cancelAnimationFrame(raf1)
      cancelAnimationFrame(raf2)
    }
  }, [animated])

  const handleTouchStart = (e) => { touchStartX.current = e.touches[0].clientX }
  const handleTouchEnd   = (e) => {
    if (touchStartX.current === null) return
    const delta = touchStartX.current - e.changedTouches[0].clientX
    if (Math.abs(delta) > 40) delta > 0 ? next() : prev()
    touchStartX.current = null
  }

  const dotIndex = (Math.round(index) - OFFSET + TOTAL * 10) % TOTAL

  const mobileBtn = (side) => ({
    position:        "absolute",
    top:             `${sliderTop}px`,
    [side]:          "0px",
    transform:       "translateY(-50%)",
    zIndex:          999,
    width:           "44px",
    height:          "44px",
    borderRadius:    "50%",
    backgroundColor: "#ffffff",
    border:          "2px solid #e5e7eb",
    boxShadow:       "0 4px 16px rgba(0,0,0,0.25)",
    display:         "flex",
    alignItems:      "center",
    justifyContent:  "center",
    cursor:          "pointer",
    padding:         "0",
  })

  return (
    <section
      style={{ position: "relative" }}
      className=" hero-section mx-auto px-4 h-full bg-gray-50  py-6 sm:py-16 grid grid-cols-1 md:grid-cols-[35%_65%] lg:grid-cols-[40%_60%] gap-6 md:gap-4 overflow-x-hidden"
    >
      {/* MOBILE arrows — absolutely on section, above everything */}
      {!isDesktop && (
        <>
          <button onClick={prev} aria-label="Previous slide" style={mobileBtn("left")}>
            <ChevronLeft size={20} strokeWidth={2.5} />
          </button>
          <button onClick={next} aria-label="Next slide" style={mobileBtn("right")}>
            <ChevronRight size={20} strokeWidth={2.5} />
          </button>
        </>
      )}

      {/* LEFT */}
      <div className="space-y-3 sm:space-y-4 mt-2 sm:mt-8 w-full min-w-0">
        <span className="font-inter  text-[2.2rem] max-sm:text-center lg:text-5xl italic block leading-snug tracking-tight">
          Empowering the Future of Education
        </span>
        <p className="lg:text-[1.4rem] font-inter text-[15px] md:text-base xs:text-sm leading-tight text-gray-700">
          Our platform is designed to help students and teachers alike by providing a comprehensive set of tools and resources to support their learning journey. Join us to experience a seamless and innovative approach to education.
        </p>
        <div className="md:hidden ">
          <Link to="/login" className="bg-sidebar inline-block mb-3 w-full text-center no-underline text-white px-3 py-2 xs:px-4 sm:px-5 sm:py-2.5 rounded-xl text-xs sm:text-sm font-medium hover:bg-blue-900 transition">
            Start Practicing
          </Link>
        </div>
      </div>

      {/* RIGHT - MOBILE OPTIMIZED */}
      <div className="flex flex-col  gap-4">

        {/* Slide track — image-only, so no background panel can pass over the image */}
        <div ref={sliderRef} style={{ overflow: "hidden", borderRadius: "16px" }}
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
        >
          <div
            className={animated ? "transition-transform duration-300 ease-in-out" : ""}
            style={{
              display:   "flex",
              alignItems: "flex-start",
              gap:       `${GAP}px`,
              transform: `translateX(-${index * CARD_STEP}px)`,
            }}
          >
            {cards.map((card, i) => {
              const isExternal = card.href.startsWith("http")

              const sharedStyle = {
                borderRadius:   "16px",
                overflow:       "hidden",
                flexShrink:     0,
                display:        "block",
                position:       "relative",
                height:         `${isDesktop ? cardHeight : imageHeight}px`,
                minWidth:       `${cardWidth}px`,
                width:          `${cardWidth}px`,
                textDecoration: "none",
              }

              const img = (
                <>
                  <img
                    src={card.image}
                    alt={card.title}
                    className="w-full h-full object-cover object-top"
                  />
                  {/* Title on the image as plain text — no background panel, so
                      nothing coloured can sweep over the image while sliding */}
                  <div className="absolute inset-x-0 bottom-0 flex items-start justify-between gap-2 p-3 sm:p-4 bg-gradient-to-t from-black/70 via-black/40 to-transparent">
                    <span className="font-semibold text-sm leading-tight text-white break-words sm:text-base drop-shadow-[0_1px_6px_rgba(0,0,0,0.9)]">
                      {card.title}
                    </span>
                    <span className="text-lg text-white drop-shadow-[0_1px_6px_rgba(0,0,0,0.9)]">→</span>
                  </div>
                </>
              )

              return isExternal ? (
                <a key={i} href={card.href} target="_blank" rel="noreferrer" style={sharedStyle}>
                  {img}
                </a>
              ) : (
                <Link key={i} to={card.href} style={sharedStyle}>
                  {img}
                </Link>
              )
            })}
          </div>
        </div>

        {/* DESKTOP: arrows + dots below */}
        {isDesktop && (
          <div className="flex items-center justify-center gap-6">
            <button onClick={prev} aria-label="Previous slide"
              className="flex w-10 h-10 rounded-full bg-white border border-gray-200 shadow items-center justify-center hover:bg-gray-50 transition">
              <ChevronLeft size={16} />
            </button>
            <div className="flex gap-2">
              {BASE_CARDS.map((_, i) => (
                <button key={i} onClick={() => setIndex(OFFSET + i)} aria-label={`Go to slide ${i + 1}`}
                  className={`h-2 rounded-full transition-all ${i === dotIndex ? "w-8 bg-blue-500" : "w-2 bg-gray-300"}`} />
              ))}
            </div>
            <button onClick={next} aria-label="Next slide"
              className="flex w-10 h-10 rounded-full bg-white border border-gray-200 shadow items-center justify-center hover:bg-gray-50 transition">
              <ChevronRight size={16} />
            </button>
          </div>
        )}

        {/* MOBILE: dots below */}
        {!isDesktop && (
          <div className="flex items-center justify-center gap-2">
            {BASE_CARDS.map((_, i) => (
              <button key={i} onClick={() => setIndex(OFFSET + i)} aria-label={`Go to slide ${i + 1}`}
                className={`h-2 rounded-full transition-all ${i === dotIndex ? "w-8 bg-blue-500" : "w-2 bg-gray-300"}`} />
            ))}
          </div>
        )}

      </div>
    </section>
  )
}
